import styled from "styled-components";
import { Card } from "@mui/material";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../../../utils/constants";

export const HeroCard = styled(Card)`
  && {
    position: relative;
    border-radius: 12px;
    box-shadow: var(--shadow-small);
    border: 1px solid var(--color-card-border);
    background: var(--hero-background);

    font-family: "Crimson Pro", sans-serif;
    padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;

    @media ${QUERIES.tabletLargeAndUp} {
      padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
    }

    @media ${QUERIES.laptopAndUp} {
      padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    }
  }
`;

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 55fr 45fr;
    align-items: start;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const HeroAccountName = styled.h1`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
  margin: 0;
`;

export const HeroSubtitle = styled.p`
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin: 0;
`;

export const BalanceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  margin-top: ${SPACING.md}px;
`;

export const BalanceLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const BalanceAmount = styled.span<{ $negative: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display-xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 0.95;
  margin-left: 0.15em;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-text-default)"};
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const StatsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 10px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
  box-shadow: var(--shadow-small);
`;

export const ActionsRow = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    gap: ${SPACING.sm}px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`;

export const KebabWrapper = styled.div`
  position: absolute;
  top: ${SPACING.md}px;
  right: ${SPACING.md}px;
  z-index: 10;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px 0;
  transition: background ${ANIMATION.duration.normal}
    ${ANIMATION.easing.easeOut};

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

export const StatLabelBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
`;

export const StatLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const StatSubLabel = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
`;

export const StatValue = styled.span<{ $color?: string }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ $color }) => $color ?? "var(--color-text-default)"};
  white-space: nowrap;
`;

export const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.xs}px;
`;

export const ChangeChip = styled.span<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 16px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-sm);
  font-weight: 600;
  white-space: nowrap;
  background-color: ${({ $negative }) =>
    $negative
      ? "color-mix(in srgb, var(--color-error) 15%, transparent)"
      : "color-mix(in srgb, var(--color-success) 15%, transparent)"};
  border: 1px solid
    ${({ $negative }) =>
      $negative
        ? "color-mix(in srgb, var(--color-error) 30%, transparent)"
        : "color-mix(in srgb, var(--color-success) 30%, transparent)"};
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-success)"};
`;

export const ChangeVsLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
`;

export const HeroStatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: ${({ $isActive }) =>
    $isActive
      ? "color-mix(in srgb, var(--color-success) 14%, transparent)"
      : "color-mix(in srgb, var(--color-text-muted) 14%, transparent)"};
  color: ${({ $isActive }) =>
    $isActive ? "var(--color-success)" : "var(--color-text-muted)"};

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;
